type LegacyQueryValue = string | number | boolean | null | undefined;

type LegacyQueryParams = Record<string, LegacyQueryValue>;

/**
 * Temporary helper that hands control back to the AngularJS router by updating the hash.
 * Preserves legacy Vendor Management URLs during the incremental React migration.
 */
export function navigateToLegacyVendorRoute(path: string, queryParams?: LegacyQueryParams) {
  if (!path) {
    return;
  }

  const trimmedPath = path.trim();
  if (!trimmedPath) {
    return;
  }

  const hasHash = trimmedPath.startsWith('#');
  const withoutHash = hasHash ? trimmedPath.slice(1) : trimmedPath;
  
  // Split path and existing query string
  const [pathOnly, existingQuery] = withoutHash.split('?');
  const normalizedPath = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;

  let queryString = existingQuery || ''; // Preserve existing query string from path
  
  // Append additional query params if provided
  if (queryParams) {
    const pairs = Object.entries(queryParams).filter(([, value]) => value !== null && value !== undefined);
    if (pairs.length > 0) {
      const additionalQuery = pairs
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
        .join('&');
      
      if (queryString) {
        queryString += '&' + additionalQuery;
      } else {
        queryString = additionalQuery;
      }
    }
  }

  const hashTarget = queryString ? `#${normalizedPath}?${queryString}` : `#${normalizedPath}`;
  window.location.hash = hashTarget;
}

export function syncLegacyVendorRoute(path: string) {
  if (typeof window === 'undefined' || !path) {
    return;
  }

  if (!window.history?.replaceState || !window.location.hash) {
    return;
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path.replace(/^\/+/, '')}`;
  const nextHash = `#${normalizedPath}`;
  
  // Decode current hash to compare properly (handles %20 vs space differences)
  const currentHash = decodeURIComponent(window.location.hash);
  const targetHash = decodeURIComponent(nextHash);
  
  if (currentHash === targetHash) {
    return;
  }

  // Only sync if paths differ significantly (ignore encoding differences)
  const currentPath = currentHash.replace(/^#/, '').split('?')[0];
  const targetPath = targetHash.replace(/^#/, '').split('?')[0];
  if (currentPath === targetPath) {
    return;
  }

  const { pathname, search } = window.location;
  window.history.replaceState(null, '', `${pathname}${search}${nextHash}`);
}
